import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fireConfetti } from "@/lib/confetti";
import { useAuth } from "@/context/AuthContext";
import { showXPToast } from "@/lib/xpToast";

export default function DailyMissionsWidget() {
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const { refreshUser } = useAuth();

  useEffect(() => {
    api.get("/missions/today").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/missions/claim");
      setStatus((prev) => ({ ...prev, claimed: true }));
      fireConfetti();
      showXPToast(data.bonus_xp, data.bonus_coins, "Daily Missions Complete!");
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't claim bonus right now");
    } finally {
      setClaiming(false);
    }
  };

  if (!status) return null;
  const doneCount = status.missions.filter((m) => m.done).length;

  return (
    <div className="glass-panel rounded-2xl p-6" data-testid="daily-missions-widget">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
          <Gift className="w-3.5 h-3.5 text-indigo-400" /> Daily Missions
        </span>
        <span className="text-xs font-bold text-indigo-300 tabular-nums">{doneCount}/{status.missions.length}</span>
      </div>
      <div className="space-y-2.5">
        {status.missions.map((m) => (
          <div key={m.id} data-testid={`mission-item-${m.id}`} className="flex items-center gap-2.5">
            {m.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <Circle className="w-4 h-4 text-slate-500 shrink-0" />}
            <span className={`text-sm ${m.done ? "text-slate-300 line-through" : "text-slate-400"}`}>{m.label}</span>
          </div>
        ))}
      </div>
      <motion.button
        data-testid="claim-daily-mission-bonus-btn"
        disabled={!status.all_done || status.claimed || claiming}
        onClick={claim}
        whileTap={status.all_done && !status.claimed ? { scale: 0.97 } : {}}
        className={`mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors ${
          status.claimed
            ? "bg-emerald-500/10 text-emerald-400 cursor-default"
            : status.all_done
              ? "bg-primary hover:bg-primary/90 text-white"
              : "bg-white/5 text-slate-500 cursor-not-allowed"
        }`}
      >
        {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : status.claimed ? (
          <>Claimed today <CheckCircle2 className="w-4 h-4" /></>
        ) : (
          <>Claim +{status.bonus_xp} XP · +{status.bonus_coins} coins</>
        )}
      </motion.button>
    </div>
  );
}
